import {
  NodeKind,
  TransformKind,
  type ImageNode,
  type OrientationParams,
  type TransformStack,
} from '@/core/document'
import {
  IDENTITY_ORIENTATION,
  MAX_STRAIGHTEN_DEG,
  MIN_STRAIGHTEN_DEG,
  STRAIGHTEN_SNAP_DEG,
  assertOrientationParams,
  centeredPresetRectInside,
  constrainRectToRatioInside,
  largestRectForRatioInside,
  mapRectBetweenOrientations,
  normalizedQuarterTurns,
  orientationCropBounds,
} from '@/core/geometry'
import { useEditSession } from '@/core/store/editSession'
import { EditAction } from '@/core/history'
import { selectEffectiveRatio, selectSelectedSource } from '@/core/store/selectors'
import { selectSelectedCrop, selectSelectedImageNode } from '@/tools/crop/selectors'
import { selectSelectedOrientation } from '@/tools/orientation/selectors'

export enum QuarterTurnDirection {
  Left = -1,
  Right = 1,
}

export enum FlipAxis {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

function transformsWithOrientation(
  node: ImageNode,
  orientation: OrientationParams,
  cropRect: ReturnType<typeof selectSelectedCrop>['rect'],
): TransformStack {
  const transforms: TransformStack = {
    ...node.transforms,
    [TransformKind.Crop]: { rect: cropRect },
  }
  if (orientation.quarterTurns === 0
    && !orientation.flipHorizontal
    && !orientation.flipVertical
    && orientation.straightenDeg === 0) {
    delete transforms[TransformKind.Orientation]
  } else {
    transforms[TransformKind.Orientation] = { ...orientation }
  }
  return transforms
}

function updateSelectedImage(orientation: OrientationParams, cropRect: ReturnType<typeof selectSelectedCrop>['rect']): void {
  assertOrientationParams(orientation)
  const state = useEditSession.getState()
  const node = selectSelectedImageNode(state)
  state.updateNode(node.id, (current) => {
    if (current.kind !== NodeKind.Image) {
      throw new Error(`Orientation update requires an image node, received "${current.kind}"`)
    }
    return { ...current, transforms: transformsWithOrientation(current, orientation, cropRect) }
  })
}

export function turnSelectedImage(direction: QuarterTurnDirection): void {
  if (!Object.values(QuarterTurnDirection).includes(direction)) {
    throw new Error(`Unknown quarter-turn direction: "${String(direction)}"`)
  }
  const state = useEditSession.getState()
  const source = selectSelectedSource(state)
  const crop = selectSelectedCrop(state)
  const ratio = selectEffectiveRatio(state)
  const current = selectSelectedOrientation(state)
  const next: OrientationParams = {
    ...current,
    quarterTurns: normalizedQuarterTurns(current.quarterTurns + direction),
  }
  const mapped = mapRectBetweenOrientations(crop.rect, source, current, next)
  const nextBounds = orientationCropBounds(source, next)
  updateSelectedImage(next, centeredPresetRectInside(mapped, nextBounds, ratio))
  useEditSession.getState().commitHistory(EditAction.RotatePhoto)
}

export function flipSelectedImage(axis: FlipAxis): void {
  if (!Object.values(FlipAxis).includes(axis)) throw new Error(`Unknown flip axis: "${String(axis)}"`)
  const state = useEditSession.getState()
  const crop = selectSelectedCrop(state)
  const current = selectSelectedOrientation(state)
  const next: OrientationParams = axis === FlipAxis.Horizontal
    ? { ...current, flipHorizontal: !current.flipHorizontal }
    : { ...current, flipVertical: !current.flipVertical }
  updateSelectedImage(next, crop.rect)
  useEditSession.getState().commitHistory(EditAction.FlipPhoto)
}

export function setSelectedStraighten(requestedDegrees: number): void {
  if (!Number.isFinite(requestedDegrees)
    || requestedDegrees < MIN_STRAIGHTEN_DEG
    || requestedDegrees > MAX_STRAIGHTEN_DEG) {
    throw new Error(`Straighten value must be between ${MIN_STRAIGHTEN_DEG} and ${MAX_STRAIGHTEN_DEG}`)
  }
  const straightenDeg = Math.abs(requestedDegrees) <= STRAIGHTEN_SNAP_DEG
    ? 0
    : Math.round(requestedDegrees * 10) / 10
  const state = useEditSession.getState()
  const source = selectSelectedSource(state)
  const crop = selectSelectedCrop(state)
  const ratio = selectEffectiveRatio(state)
  const next = { ...selectSelectedOrientation(state), straightenDeg }
  const bounds = orientationCropBounds(source, next)
  updateSelectedImage(next, constrainRectToRatioInside(crop.rect, bounds, ratio))
}

export function commitSelectedStraighten(): void {
  useEditSession.getState().commitHistory(EditAction.StraightenPhoto)
}

export function resetSelectedOrientation(): void {
  const state = useEditSession.getState()
  const source = selectSelectedSource(state)
  const ratio = selectEffectiveRatio(state)
  updateSelectedImage(IDENTITY_ORIENTATION, largestRectForRatioInside(
    orientationCropBounds(source, IDENTITY_ORIENTATION),
    ratio,
  ))
  useEditSession.getState().commitHistory(EditAction.ResetOrientation)
}
